<template>
  <div class="d-flex flex-column bg-white plan-preview">
    <div class="mb-3 header">
      <p v-if="plan" class="subscription-title">{{ plan.title }}</p>
      <p class="subscription-subtitle">Administrer abonnementet ditt</p>
    </div>
    <div class="d-flex flex-column">
      <div class="d-flex flex-row justify-content-between">
        <p>Status:</p>
        <div
          class="subscription-status"
          :class="statusClass"
          :aria-label="`Status: ${statusText}`"
          role="status"
          aria-live="polite"
          aria-atomic="true"
        >
          {{ statusText }}
        </div>
      </div>
      <div v-if="isCanceled" class="d-flex flex-row justify-content-between">
        <p>Kanselleringsdato:</p>
        <p>{{ formattedPendingCancellationEndDate || '-' }}</p>
      </div>
      <div v-if="isEnding" class="d-flex flex-row justify-content-between">
        <p>Sluttdato:</p>
        <p>{{ formattedEndDate || '-' }}</p>
      </div>
      <div class="d-flex flex-row justify-content-between">
        <p>Pris:</p>
        <p>{{ plan && plan.price ? `${plan.price} / mnd` : '-' }}</p>
      </div>
      <div v-if="hasSubscriptionInvoiceInfo" class="subscription-info-box">
        <i class="icon budicon-performance-board subscription-info-icon"></i>
        <div class="subscription-info-content">
          <p class="subscription-info-title">{{ invoiceCountSummary }}</p>
          <ul class="subscription-info-list">
            <li
              v-for="(order, index) in subscriptionIssuedOrders"
              :key="order.id || `${order.typeOf}-${order.period}-${(order.date && order.date.due) || index}`"
            >
              {{ formatIssuedOrderLine(order) }}
            </li>
          </ul>
          <p class="subscription-info-text">
            Endringer på abonnementet påvirkes av hvilke fakturaer som fremdeles kreves inn.
            Avslutning av abonnementet kan tidligst skje {{ earliestSubscriptionEndDate || '-' }}.
          </p>
        </div>
      </div>
    </div>
    <div v-if="buttons.length" class="button-container">
      <button
        v-for="button in buttons"
        :key="button.text"
        class="btn"
        :class="button.classes"
        :disabled="button.disabled"
        @click="button.func"
      >
        {{ button.text }}
      </button>
    </div>
  </div>
</template>

<script>
import {
  getIssuedAccountingOrders,
  getScheduledDowngradeDateFromOrders,
} from '@/lib/tenancySubscriptionDates';

export default {
  name: 'TenancySubscriptionPlanPreview',
  props: {
    plan: Object,
    buttons: {
      type: Array,
      default: () => [],
      validator(buttons) {
        return buttons.every(button =>
          button &&
          typeof button === 'object' &&
          typeof button.text === 'string' &&
          typeof button.func === 'function' &&
          (button.classes === undefined || typeof button.classes === 'string') &&
          (button.disabled === undefined || typeof button.disabled === 'boolean')
        );
      }
    },
    tenancyData: {
      type: Object,
      default: () => ({})
    },
    orders: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    parseDate(dateString) {
      if (!dateString) return null;
      const date = new Date(dateString);
      return isNaN(date.getTime()) ? null : date;
    },
    formatNorwegianDate(dateValue) {
      const date = this.parseDate(dateValue);
      if (!date) return null;
      const locale = (this.$i18n?.locale || 'nb_NO').replace('_', '-');

      return date.toLocaleDateString(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
    formatIssuedOrderLine(order) {
      const prefix = order?.typeOf === 'rentInvoice' ? 'Husleie for' : 'Tilleggsfaktura for';
      const period = order?.period || 'ukjent periode';
      const dueDate = this.formatNorwegianDate(order?.date?.due) || '-';

      return `${prefix} ${period} med forfall ${dueDate}.`;
    }
  },
  computed: {
    isCanceled() {
      const subscriptionStatus = this.tenancyData?.subscription?.status;
      if (subscriptionStatus === 'canceled') {
        return true;
      }

      const endDate = this.parseDate(this.tenancyData?.endDate);
      if (!endDate) {
        return false;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return endDate < today;
    },
    // isEnding if endDate is current or next month
    isEnding() {
      if (!this.tenancyData?.endDate) return false;
      const endDate = this.parseDate(this.tenancyData.endDate);
      if (!endDate) return false;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const currentMonth = today.getMonth();
      const currentYear = today.getFullYear();
      const endMonth = endDate.getMonth();
      const endYear = endDate.getFullYear();

      const isCurrentMonth = endYear === currentYear && endMonth === currentMonth;
      const isNextMonth = (endYear === currentYear && endMonth === currentMonth + 1) ||
        (endYear === currentYear + 1 && currentMonth === 11 && endMonth === 0);
      return endDate >= today && (isCurrentMonth || isNextMonth);
    },
    isActive() {
      return !this.isCanceled && !this.isEnding;
    },
    statusText() {
      if (this.isCanceled) return 'Avsluttet';
      if (this.isEnding) return 'Avsluttes';
      return 'Aktiv';
    },
    statusClass() {
      if (this.isCanceled) return 'canceled';
      if (this.isEnding) return 'ending';
      return 'active';
    },
    pendingCancellationEndDate() {
      if (!this.tenancyData || !this.tenancyData.subscription || !this.tenancyData.subscription.pendingCancelation) {
        return null;
      }
      return this.tenancyData.subscription.pendingCancelation.endDate || null;
    },
    formattedPendingCancellationEndDate() {
      return this.formatNorwegianDate(this.pendingCancellationEndDate);
    },
    formattedEndDate() {
      return this.formatNorwegianDate(this.tenancyData?.endDate);
    },
    subscriptionIssuedOrders() {
      return getIssuedAccountingOrders(this.orders || []);
    },
    hasSubscriptionInvoiceInfo() {
      return this.subscriptionIssuedOrders.length > 0;
    },
    invoiceCountSummary() {
      const count = this.subscriptionIssuedOrders.length;
      return `Vi krever for tiden inn ${count} faktura${count === 1 ? '' : 'er'} for dette leieforholdet:`;
    },
    earliestSubscriptionEndDate() {
      const scheduledDowngradeDate = getScheduledDowngradeDateFromOrders(this.subscriptionIssuedOrders);
      if (!scheduledDowngradeDate) {
        return null;
      }
      return this.formatNorwegianDate(scheduledDowngradeDate);
    }
  },
};
</script>
<style scoped lang="scss">
.plan-preview {
  min-width: 500px;
  padding: 16px;
  width: fit-content;
  color: #3D3F54;
  border-radius: $border-radius;
  border: 1px solid #E2E3E9;
  font-size: 16px;
  font-family: 'EuclidCircularB';

  @media (max-width: 768px) {
    min-width: unset;
  }

  .header {
    display: flex;
    flex-direction: column;

    .subscription-title {
      font-family: 'EuclidCircularB-Medium';
      font-size: 16px;
      margin-bottom: .2rem;
    }

    .subscription-subtitle {
      font-size: 16px;
      color: #767891;
    }
  }

  .subscription-status {
    height: fit-content;
    border-radius: 8px;
    padding: 6px 10px;
    background-color: #E94D5F;
    font-size: 12px;
    line-height: 16px;
    font-family: 'EuclidCircularB-Medium';
    color: white;

    &.active {
      background-color: #00A373;
    }

    &.ending {
      background-color: #f59e0b;
    }

    &.canceled {
      background-color: #ef4444;
    }
  }

  .button-container {
    display: flex;
    flex-direction: column;
    gap: 12px;

    button {
      flex: 1;
    }
  }

  .subscription-info-box {
    display: flex;
    align-items: flex-start;
    gap: .5rem;
    border: 1px solid #00A37333;
    border-radius: .5rem;
    background-color: rgb(236, 248, 242);
    padding: 1rem;
    margin-bottom: 1rem;
    font-size: 14px;
    line-height: 1.4;

    .subscription-info-content {
      flex: 1;
      min-width: 0;
    }

    .subscription-info-title {
      margin-bottom: 8px;
      font-family: 'EuclidCircularB-Medium';
    }

    .subscription-info-icon {
      color: #00A373;
      font-size: 16px;
    }

    .subscription-info-list {
      margin: 0 0 8px 16px;
      padding: 0;

      li {
        margin-bottom: 4px;
      }
    }

    .subscription-info-text {
      margin-bottom: 0;
    }
  }
}
</style>
